import React, { useState, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeProvider';
import { newtonProtocolsAPI } from '../../api';
import FileUploadModal from './FileUploadModal';

const DataSourceModal = ({ source, onSave, onCancel, loading }) => {
  const { mode } = useTheme();
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'google_drive',
    connection_config: {}
  });
  
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [showFileUpload, setShowFileUpload] = useState(false);
  
  useEffect(() => {
    if (source) {
      setFormData({
        name: source.name,
        type: source.type,
        connection_config: source.connection_config || {}
      });
    }
  }, [source]);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  const handleConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      connection_config: {
        ...prev.connection_config,
        [field]: value
      }
    }));
    
    // Limpiar error del campo
    if (errors[`config_${field}`]) {
      setErrors(prev => ({ ...prev, [`config_${field}`]: null }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    
    if (formData.type === 'google_drive') {
      if (!formData.connection_config.spreadsheet_id) {
        newErrors.config_spreadsheet_id = 'El ID de la hoja es requerido';
      }
      if (!formData.connection_config.credentials_json) {
        newErrors.config_credentials_json = 'Las credenciales son requeridas';
      }
    } else if (formData.type === 'mysql') {
      if (!formData.connection_config.host) {
        newErrors.config_host = 'El host es requerido';
      }
      if (!formData.connection_config.database) {
        newErrors.config_database = 'La base de datos es requerida';
      }
      if (!formData.connection_config.table) {
        newErrors.config_table = 'La tabla es requerida';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleTestConnection = async () => {
    if (formData.type !== 'google_drive') {
      alert('La prueba de conexión solo está disponible para Google Drive por ahora');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await newtonProtocolsAPI.dataSources.testConnection({
        credentials_json: formData.connection_config.credentials_json,
        spreadsheet_id: formData.connection_config.spreadsheet_id
      });
      
      setTestResult({
        success: true,
        message: response.data.message,
        details: response.data
      });
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Error de conexión',
        details: err.response?.data
      });
    } finally {
      setTesting(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await onSave(formData);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error guardando la fuente');
    }
  };
  
  const parseCredentialsJSON = (text) => {
    try {
      const parsed = JSON.parse(text);
      handleConfigChange('credentials_json', parsed);
      setErrors(prev => ({ ...prev, config_credentials_json: null }));
    } catch (e) {
      setErrors(prev => ({ ...prev, config_credentials_json: 'JSON inválido' }));
    }
  };
  
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: 'var(--spacing-lg)',
      animation: 'fadeIn 0.2s ease-out'
    },
    modal: {
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-primary)' : 'var(--color-white)',
      borderRadius: 'var(--border-radius-xl)',
      maxWidth: '700px',
      width: '100%',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: mode === 'dark' ? 'var(--dark-shadow-xl)' : 'var(--shadow-xl)',
      animation: 'slideInUp 0.3s ease-out',
      border: mode === 'dark' ? '1px solid var(--dark-border-primary)' : 'none'
    },
    header: {
      padding: 'var(--spacing-xl)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`
    },
    title: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      marginBottom: 0
    },
    body: {
      padding: 'var(--spacing-xl)',
      overflowY: 'auto',
      flex: 1
    },
    footer: {
      padding: 'var(--spacing-xl)',
      borderTop: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      display: 'flex',
      gap: 'var(--spacing-md)',
      justifyContent: 'flex-end'
    },
    formGroup: {
      marginBottom: 'var(--spacing-lg)'
    },
    label: {
      display: 'block',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-dark-gray)',
      marginBottom: 'var(--spacing-xs)'
    },
    input: {
      width: '100%',
      padding: 'var(--spacing-md)',
      fontSize: 'var(--font-size-base)',
      borderRadius: 'var(--border-radius-md)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-input-border)' : 'var(--color-light-gray)'}`,
      backgroundColor: mode === 'dark' ? 'var(--dark-input-bg)' : 'var(--color-white)',
      color: mode === 'dark' ? 'var(--dark-input-text)' : 'var(--color-black)',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box'
    },
    textarea: {
      minHeight: '120px',
      resize: 'vertical',
      fontFamily: 'monospace',
      fontSize: 'var(--font-size-sm)'
    },
    error: {
      color: '#FF6B35',
      fontSize: 'var(--font-size-sm)',
      marginTop: 'var(--spacing-xs)'
    },
    testResult: {
      padding: 'var(--spacing-md)',
      borderRadius: 'var(--border-radius-md)',
      marginTop: 'var(--spacing-md)',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-sm)'
    },
    testSuccess: {
      backgroundColor: 'rgba(56, 193, 114, 0.1)',
      border: '1px solid #38C172',
      color: '#38C172'
    },
    testError: {
      backgroundColor: 'rgba(255, 107, 53, 0.1)',
      border: '1px solid #FF6B35',
      color: '#FF6B35'
    },
    helpText: {
      fontSize: 'var(--font-size-sm)',
      color: mode === 'dark' ? 'var(--dark-text-tertiary)' : 'var(--color-medium-gray)',
      marginTop: 'var(--spacing-xs)'
    }
  };
  
  return (
    <div style={styles.overlay} onClick={(e) => {
      if (e.target === e.currentTarget) onCancel();
    }}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            {source ? '✏️ Editar Fuente' : '➕ Nueva Fuente de Datos'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            {/* Nombre */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Nombre de la fuente *</label>
              <input
                type="text"
                style={styles.input}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ej: Reporte Ventas 2025"
              />
              {errors.name && <div style={styles.error}>{errors.name}</div>}
            </div>
            
            {/* Tipo */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Tipo de fuente *</label>
              <select
                style={styles.input}
                value={formData.type}
                onChange={(e) => {
                  const newType = e.target.value;
                  handleInputChange('type', newType);
                  
                  // Si selecciona file, abrir modal de upload
                  if (newType === 'file') {
                    setShowFileUpload(true);
                  }
                }}
              >
                <option value="google_drive">📁 Hojas de Cálculo de Google Drive</option>
                <option value="mysql">🗄️ Base de Datos MySQL</option>
                <option value="file">📤 Archivo CSV/Excel</option>
              </select>
            </div>
            
            {/* Mensaje para archivo */}
            {formData.type === 'file' && (
              <div style={{
                padding: 'var(--spacing-md)',
                backgroundColor: mode === 'dark' ? 'rgba(41, 181, 232, 0.1)' : 'rgba(41, 181, 232, 0.05)',
                borderRadius: 'var(--border-radius-md)',
                border: '1px solid #29B5E8',
                marginBottom: 'var(--spacing-lg)',
                textAlign: 'center'
              }}>
                <p style={{
                  margin: 0,
                  color: '#29B5E8',
                  fontSize: 'var(--font-size-sm)'
                }}>
                  📤 Haz clic en "Subir Archivo" para seleccionar tu archivo CSV o Excel
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowFileUpload(true)}
                  style={{ marginTop: 'var(--spacing-md)' }}
                >
                  📤 Subir Archivo
                </button>
              </div>
            )}
            
            {/* Configuración según tipo */}
            {formData.type === 'google_drive' && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>ID de la Hoja de Cálculo *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.connection_config.spreadsheet_id || ''}
                    onChange={(e) => handleConfigChange('spreadsheet_id', e.target.value)}
                    placeholder="1ABC...xyz"
                  />
                  <div style={styles.helpText}>
                    El ID está en la URL: docs.google.com/spreadsheets/d/<strong>ESTE_ES_EL_ID</strong>/edit
                  </div>
                  {errors.config_spreadsheet_id && <div style={styles.error}>{errors.config_spreadsheet_id}</div>}
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nombre de la Hoja (Opcional)</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.connection_config.sheet_name || ''}
                    onChange={(e) => handleConfigChange('sheet_name', e.target.value)}
                    placeholder="Hoja1"
                  />
                  <div style={styles.helpText}>
                    Deja vacío para usar la primera hoja
                  </div>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Credenciales JSON de la Cuenta de Servicio *</label>
                  <textarea
                    style={{...styles.input, ...styles.textarea}}
                    value={typeof formData.connection_config.credentials_json === 'object' 
                      ? JSON.stringify(formData.connection_config.credentials_json, null, 2) 
                      : formData.connection_config.credentials_json || ''}
                    onChange={(e) => parseCredentialsJSON(e.target.value)}
                    placeholder='{"type": "service_account", "project_id": "...", ...}'
                  />
                  <div style={styles.helpText}>
                    Pega aquí el contenido completo del archivo JSON de tu cuenta de servicio de Google
                  </div>
                  {errors.config_credentials_json && <div style={styles.error}>{errors.config_credentials_json}</div>}
                </div>
                
                {/* Botón de prueba de conexión */}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleTestConnection}
                  disabled={testing || loading}
                  style={{ width: '100%' }}
                >
                  {testing ? '⏳ Probando conexión...' : '🔍 Probar Conexión'}
                </button>
                
                {/* Resultado de la prueba */}
                {testResult && (
                  <div style={{
                    ...styles.testResult,
                    ...(testResult.success ? styles.testSuccess : styles.testError)
                  }}>
                    <span>{testResult.success ? '✓' : '✗'}</span>
                    <div>
                      <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                        {testResult.message}
                      </div>
                      {testResult.details && testResult.success && (
                        <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--spacing-xs)' }}>
                          <div>Hoja: {testResult.details.sheet_title}</div>
                          <div>Pestañas: {testResult.details.sheet_names?.join(', ')}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {formData.type === 'mysql' && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Host *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.connection_config.host || ''}
                    onChange={(e) => handleConfigChange('host', e.target.value)}
                    placeholder="localhost o IP"
                  />
                  {errors.config_host && <div style={styles.error}>{errors.config_host}</div>}
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Puerto</label>
                  <input
                    type="number"
                    style={styles.input}
                    value={formData.connection_config.port || '3306'}
                    onChange={(e) => handleConfigChange('port', e.target.value)}
                    placeholder="3306"
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Base de Datos *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.connection_config.database || ''}
                    onChange={(e) => handleConfigChange('database', e.target.value)}
                    placeholder="nombre_base_datos"
                  />
                  {errors.config_database && <div style={styles.error}>{errors.config_database}</div>}
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Tabla *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.connection_config.table || ''}
                    onChange={(e) => handleConfigChange('table', e.target.value)}
                    placeholder="nombre_tabla"
                  />
                  {errors.config_table && <div style={styles.error}>{errors.config_table}</div>}
                </div>
                
                <div style={{...styles.helpText, padding: 'var(--spacing-md)', backgroundColor: mode === 'dark' ? 'rgba(255, 182, 53, 0.1)' : 'rgba(255, 182, 53, 0.05)', borderRadius: 'var(--border-radius-md)', border: '1px solid rgba(255, 182, 53, 0.3)'}}>
                  ℹ️ <strong>Nota:</strong> La aplicación utilizará las credenciales de la base de datos principal configurada en el servidor.
                </div>
              </>
            )}
          </div>
          
          <div style={styles.footer}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onCancel}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? '⏳ Guardando...' : source ? '💾 Actualizar' : '✅ Crear Fuente'}
            </button>
          </div>
        </form>
      </div>
      
      {/* File Upload Modal */}
      {showFileUpload && (
        <FileUploadModal
          onSuccess={(uploadedFile) => {
            // Cerrar el modal de upload
            setShowFileUpload(false);
            // Cerrar este modal también
            onCancel();
            // Recargar la lista de fuentes (esto se maneja desde el componente padre)
          }}
          onCancel={() => {
            setShowFileUpload(false);
            // Resetear tipo a google_drive si cancela
            handleInputChange('type', 'google_drive');
          }}
        />
      )}
    </div>
  );
};

export default DataSourceModal;

